import { mount } from 'svelte';
import App from './App.svelte';
import './app.css';

/**
 * Mounts the application once the page has finished loading.
 *
 * Building the editor measures it, and a forced layout while the page is still
 * loading is what Firefox warns about: "Layout was forced before the page was
 * fully loaded. If stylesheets are not yet loaded this may cause a flash of
 * unstyled content." There is nothing to wait for here - the styles, the
 * interface and the content are all on the page already - so waiting for the
 * load event costs nothing, and the warning is about a flash that cannot happen
 * in a build where the stylesheets arrive before this module runs.
 */
function whenLoaded(run: () => void): void {
  if (document.readyState === 'complete') {
    run();
    return;
  }
  window.addEventListener('load', run, { once: true });
}

const target = document.getElementById('app');
if (target !== null) {
  whenLoaded(() => mount(App, { target }));
}

// The whole game is static, so after one visit it works with no network at
// all. Registration is skipped in development, where a cache would only get in
// the way of hot reloading. A registration that fails (an old browser, a
// storage quota) costs the offline mode and nothing else, so it is ignored.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch(() => {});
  });
}
